// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type { StrongOmit, PartCatalog } from '@evolvemep/api-ts';
import { Button } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { CreateEditPartFields, type CreateEditPartFormProps } from 'modules/Shop/CatalogSetup/CreateEditPartFields';
import {
  usePartEditor,
  type CreateEditPartFormType,
  PartEditorProvider,
} from 'modules/Shop/CatalogSetup/PartEditor/usePartEditor';
import type { CategoryPath } from 'modules/Shop/CatalogSetup/useCategoryPath';

const NewPartForm = ({ onClose, ...props }: CreateEditPartFormProps) => {
  const { form, publishPart } = usePartEditor();
  const onSubmit = async (formValues: CreateEditPartFormType) => {
    const part = await publishPart({ formValues });
    props.onPartUpdated?.(part);
  };
  useFormModal(form, onSubmit);
  return <CreateEditPartFields {...props} includeAssemblyButton onClose={onClose} />;
};

type CreatePartModalProps = StrongOmit<CreateEditPartFormProps, 'onClose' | 'onPartUpdated'> & {
  partCatalog: PartCatalog;
  categoryPath?: CategoryPath;
  onPartCreated: CreateEditPartFormProps['onPartUpdated'];
};

export const CreatePartModal = ({ partCatalog, categoryPath, onPartCreated, ...props }: CreatePartModalProps) => {
  const [opened, setOpened] = useState(false);

  return (
    <>
      <Button onClick={() => setOpened(true)} leftSection={<EvolveIcon icon="Add" size="sm" />}>
        New part
      </Button>
      <FormModal
        title="New part"
        opened={opened}
        onClose={() => setOpened(false)}
        type="submit"
        confirmationText="Create"
      >
        <PartEditorProvider partCatalog={partCatalog} partCategory={categoryPath?.[categoryPath.length - 1]}>
          <NewPartForm {...props} onClose={() => setOpened(false)} onPartUpdated={onPartCreated} />
        </PartEditorProvider>
      </FormModal>
    </>
  );
};
